import { colorToRGBA } from "src/utils/colorToRgba";
import { useMemo, useRef } from "react";
import styled from "styled-components";

import { Color, Flex } from "src/elements";
import { zIndexLevel } from "src/utils";
import { useHoverClickable } from "src/utils/hoverClickable";
import { AnchorLayer } from "../AnchorLayer/AnchorLayer";
import { useAnchorLayer } from "../AnchorLayer/store/hooks";
import { InputTitle } from "../Input/components/InputTitle";
import { Dropdown } from "./components/Dropdown";
import { Option } from "./components/Option";
import { selectHeight, selectHeightSmall } from "./store/constants";
import { SelectOption } from "./store/types";

const SelectContainer = styled(Flex)<{
  width?: string;
  isHovered: boolean;
  isDisabled: boolean;
}>`
  position: relative;
  width: ${({ width }) => width};
  transition-property: box-shadow;
  transition-duration: 100ms;
  transition-timing-function: ease;

  box-shadow: ${({ isHovered }) => isHovered && `0px 0px 7px ${colorToRGBA(Color.spaceGray, 0.3)}`};

  pointer-events: ${({ isDisabled }) => isDisabled && "none"};
`;

const SelectElement = styled(Flex)<{ isDisabled: boolean; size?: "small" }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  height: ${({ size }) => (size === "small" ? `${selectHeightSmall}px` : `${selectHeight}px`)};
  border: ${({ isDisabled }) =>
    `1px solid ${(isDisabled && Color.textDisabled) || Color.spaceGray}`};
  border-radius: 4px;
  cursor: pointer;

  // Target placeholder option
  > div:last-of-type {
    border-radius: 4px;
  }
`;

interface Props<T> {
  options: SelectOption<T>[];
  value: T;
  setValue: (value: T) => void;
  placeholder?: string;
  title?: string;
  width?: string;
  size?: "small";
  isSearchable?: boolean;
  isLoading?: boolean;
  isRequired?: boolean;
  isDisabled?: boolean;
  pinnedValues?: T[];
  onTogglePin?: (value: T) => void;
  getPinActionLabel?: (label: string, isPinned: boolean) => string;
}

export const Select = <T,>({
  options,
  value,
  setValue,
  placeholder,
  title,
  width = "100%",
  size,
  isSearchable = false,
  isLoading = false,
  isRequired = false,
  isDisabled = false,
  pinnedValues,
  onTogglePin,
  getPinActionLabel,
}: Props<T>) => {
  const refSelect = useRef<HTMLDivElement>(null);
  const { isHovered } = useHoverClickable(refSelect);
  const { refLayer, isRendering, isUsingClick, layerState, hideAnchorLayer } = useAnchorLayer({
    refTrigger: refSelect,
    isTopOfParent: true,
    isUsingClick: true,
  });
  const isOpen = layerState !== null;

  const selectedOption = useMemo(() => {
    return options.find((option) => option.value === value);
  }, [options, value]);

  const selectedValueLabel = selectedOption?.label;

  const onSelect = (value: T) => {
    setValue(value);
    hideAnchorLayer();
  };

  return (
    <SelectContainer
      width={width}
      isHovered={isHovered && !isOpen}
      isDisabled={isDisabled}
      aria-disabled={isDisabled}
    >
      <InputTitle
        label={title}
        isActive={isOpen !== null}
        isDisabled={isDisabled}
        isRequired={isRequired}
        zIndex={isOpen ? zIndexLevel.ABSOLUTE_LAYER + 1 : undefined}
      />

      <SelectElement
        ref={refSelect}
        isDisabled={isDisabled}
        size={size}
        data-testid={title?.toLowerCase()}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={title || placeholder || "select"}
        aria-disabled={isDisabled}
      >
        <Option
          label={selectedValueLabel || (placeholder && `Select ${placeholder}...`) || ""}
          icon={{
            name: isLoading ? "spinner" : "triangleDown",
            color: isDisabled ? Color.textDisabled : Color.primary,
          }}
          leftIcon={selectedOption?.icon}
          isPlaceholder={!selectedValueLabel}
          isDisabled={isDisabled}
          isOpener
          size={size}
        />
      </SelectElement>

      <AnchorLayer
        refLayer={refLayer}
        layerState={layerState}
        isRendering={isRendering}
        isUsingClick={isUsingClick}
        isUsingParentWidth
        hideAnchorLayer={hideAnchorLayer}
      >
        <Dropdown
          value={value}
          name={title?.toLowerCase() || "select-dropdown"}
          options={options}
          onSelect={onSelect}
          isLoading={isLoading}
          isSearchable={isSearchable}
          closeDropdown={hideAnchorLayer}
          size={size}
          pinnedValues={pinnedValues}
          onTogglePin={onTogglePin}
          getPinActionLabel={getPinActionLabel}
        />
      </AnchorLayer>
    </SelectContainer>
  );
};
